/*************************************************************************
 *
 * c20g CONFIDENTIAL
 * __________________
 *
 *  [2007] - [2021] Counterpoint Consulting, Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Counterpoint Consulting Incorporated.
 * The intellectual and technical concepts contained
 * herein are proprietary to Counterpoint Consulting Incorporated
 * and its suppliers and may be covered by U.S. and Foreign Patents,
 * patents in process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Counterpoint Consulting Incorporated.
 */

import _ from 'lodash';
import Backbone from 'backbone';
import { View } from 'backbone.marionette';

import Session from '@/Session';
import showModal from '@/util/showModal';
import showErrorMessage from '@/util/showErrorMessage';
import showSuccessMessage from '@/util/ShowSuccessMessage';
import CommandRowCollectionView from '@/views/CommandRowCollectionView';
import ConfirmationModalView from '@/views/ConfirmationModalView';

import SelectCommandModalView from '../SelectCommandModalView';
import RequestUserCommandAccess from '../../services/RequestUserCommandAccess';
import RemoveCommandAccess from '../../../external-dashboard/services/RemoveCommandAccess';

import './AddCommandView.css';

import tmplAddCommandView from './AddCommandView.hbs';

const AddCommandView = View.extend({
  template: tmplAddCommandView,

  className: 'add-command-view',

  ui: {
    commandsList: '.commands-list',
    removeCommand: '.remove-command',
    noCommandsError: '.no-commands-selected-error'
  },

  regions: {
    commandsList: '@ui.commandsList',
    modalRegion: '#modal-region'
  },

  events: {
    'click .btn-add-command': 'addCommand',
    'click @ui.removeCommand': 'removeCommand'
  },

  childViewEvents: {
    'update:collection': 'validateCommandsList'
  },

  templateContext() {
    return {
      isNotAdminPage: this.model.get('isAdminPage')
    }
  },

  onRender() {
    this.commandRowCollectionView = new CommandRowCollectionView({
      collection: new Backbone.Collection()
    });
    this.showChildView('commandsList', this.commandRowCollectionView);
    if (this.model && this.model.get('externalUserCommands')) {
      const commandsToAdd = this.model.get('externalUserCommands');
      _.each(commandsToAdd, (command) => this.commandRowCollectionView.collection.unshift(command));
    }

    this.ui.noCommandsError.hide();
  },

  toggleForm(state) {
    this.$el.find(':input').prop('disabled', !state);
  },

  async removeCommand(e) {
    e.preventDefault();
    const self = this;

    try {
      this.commandID = $(e.currentTarget).next().attr('name'); // Use e.currentTarget instead of e.Target since event could be triggered by the icon inside the button

      if (this.model.get('isProfilePage') && !this.model.get('isAdminPage')) {
        if (this.commandID === parseInt(Session.user.actingAsCommandId)) {
          //gives user error if they try to delete their current actingAsCommand
          this.showActingAsCommandRemovalError();
          return;
        }

        _.each(this.commandRowCollectionView.collection.models, (model) => {
          if (self.commandID === parseInt(model.get('id'))) {
            const approvalStatusID = model.get('approvalStatusID');
            const commandName = model.get('name');

            let isApproved;
            if (approvalStatusID === '2') {
              isApproved = true;
            } else if (approvalStatusID === '1') {
              isApproved = false;
            }

            this.showCancelCommandMembershipModal(commandName, self.commandID, isApproved);
          }
        });
      } else if(this.model.get('isAdminPage')) {
        this.triggerMethod('remove:command', this.commandID);
        this.commandRowCollectionView.collection.remove(this.commandID);
      } else {
        this.commandRowCollectionView.collection.remove(this.commandID);
      }
    } catch (e) {
      this.showCommandRemovalError();
    }
  },

  showCancelCommandMembershipModal(commandName, commandID, isApproved) {
    const self = this;
    showModal({
      title: isApproved ? 'Cancel Command Membership' : 'Cancel Command Membership Request',
      view: new ConfirmationModalView({
        confirmText: 'Remove',
        model: new Backbone.Model({
          message: isApproved
            ? `Are you sure you would like to cancel your membership to Command ${commandName}? If you wish to request to join this command in the future, your request to join will go through the full approval process.`
            : `Are you sure you would like to cancel your membership request to Command ${commandName}?`,
          onConfirm() {
            return RemoveCommandAccess(commandID);
          },
          onConfirmDone() {
            isApproved
              ? self.showApprovedCommandMembershipRemovalSuccess(commandName)
              : self.showPendingCommandRequestRemovalSuccess(commandName);
            self.commandRowCollectionView.collection.remove(commandID);
          }
        })
      })
    });
  },

  showPendingCommandRequestRemovalSuccess(commandName) {
    showSuccessMessage({
      code: 'removalSuccess',
      title: 'Command Access Request Cancelled',
      message: `Your request to access Command ${commandName} has been removed.`,
      hideAfter: 5
    });
  },

  showApprovedCommandMembershipRemovalSuccess(commandName) {
    showSuccessMessage({
      code: 'removalSuccess',
      title: 'Command Access Removed',
      message: `Your access to Command ${commandName} has been removed.`,
      hideAfter: 5
    });
  },

  showCommandRemovalError() {
    showErrorMessage({
      errorCode: 'removalError',
      errorTitle: 'Command Access Removal Error',
      errorMessage: 'An error occurred while removing command. Try again or contact an administrator if your issue persists.'
    });
  },

  showActingAsCommandRemovalError() {
    showErrorMessage({
      errorCode: 'removalError',
      errorTitle: 'Command Access Removal Error',
      errorMessage:
        'The active command cannot be removed. To remove this command, please set another command to active on the dashboard screen.'
      //should they be able to switch their acting as command on the profile screen? Enhancement?
    });
  },

  async addCommand(e) {
    e?.preventDefault();
    const commandsToAdd = await showModal({
      title: 'Select Command',
      dialogClasses: 'modal-xl',
      view: new SelectCommandModalView({
        model: new Backbone.Model({
          isProfilePage: this.model.get('isProfilePage')
        })
      })
    });
    const combinedCommandLength = commandsToAdd.length + this.commandRowCollectionView.collection.length
    if(combinedCommandLength <= 10 || !this.model.get('isRegistrationPage')){
      _.each(commandsToAdd, (command) => this.commandRowCollectionView.collection.unshift(command));
    } else {
      this.showCommandLimitExceededError();
    }
   
  },

  getCommandIds() {
    return _.reduce(this.commandRowCollectionView.collection.models, (ids, model) => [model.get('id'), ...ids], []);
  },

  getNewCommandIds() {
    const commandIds = this.getCommandIds();
    const newCommandIds = [];

    _.each(this.commandRowCollectionView.collection.models, (command) => {
      _.each(commandIds, (id) => {
        if (command.get('id') === id && command.get('isNewRequest')) {
          newCommandIds.push(id)
        }
      });
    });

    return newCommandIds;
  },

  async validateCommandsList(e) {
    e?.preventDefault();
    if (this.commandRowCollectionView.collection.length < 1) {
      this.ui.noCommandsError.show();
      return false;
    } else {
      this.ui.noCommandsError.hide();
      return true;
    }
  },

  async submit() {
    const commandIds = this.getCommandIds();
    const requests = [];

    if (this.model.get('isProfilePage')) {
      _.each(this.commandRowCollectionView.collection.models, (command) => {
        _.each(commandIds, (id) => {
          if (command.get('id') === id && command.get('isNewRequest')) {
            requests.push(RequestUserCommandAccess(id));
          }
        });
      });
    }

    if (!requests) {
      return;
    }
    return await Promise.all(requests);
  },

  showCommandLimitExceededError() {
    showErrorMessage({
      errorCode: 'tcommandLimitExceeded',
      errorTitle: 'Command Limit Exceeded',
      errorMessage: 'You may only select 10 Commands at once.'
    });
  }
});

export default AddCommandView;
